
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { I18n } from '../lib/interfaces/I18n';
import { RouteTargetEntry } from '../lib/interfaces/route/RouteTargetEntry';
import deDE from '../lib/presets/resources/de-DE';
import { container } from '../lib/container';
import { Actions, ActionStates } from '../lib/Actions';
import { ContainerTypes } from '../lib/core/types/ContainerTypes';
import { isRouteTargetEntry, isResultEntry, isLatLngLiteral } from '../lib/core/guards';
import { SearchStorage } from '../lib/core/storage/SearchStorage';
import { RoutingStorage } from '../lib/core/storage/RoutingStorage';
import { SpeedProfile, PositionStation } from '../lib/interfaces/state-events/RoutingRequest';
import SpeedProfileBtn from './routing/speed-profile-btn.vue';
import { GeocoderEntry } from '../lib/interfaces/search/GeocoderEntry';
import { RequestRouting } from '../lib/core/RequestRouting';
import { SearchTypes } from '../lib/core/types/SearchTypes';
import { RouteEntry, WayPoint } from '../lib/interfaces/route/RouteEntry';
import AutoCompleteSuggestion = ym.AutoCompleteSuggestion;
import convertUnits from 'convert-units';
import { execCmd, setCmd } from '../lib/core/storage/CommandStorage';
import { allowDevices } from '../lib/core/responsive';
import { RequestGeocoder } from '../lib/core/RequestGeocoder';
import SimpleBar from './simplebar.vue';
import { getConfig } from '../lib/ConfigLoader';
import { formatAddressLine } from '../lib/core/util';

const actions = container.get<Actions>(ContainerTypes.Action);
const searchStorage = container.get<SearchStorage>(ContainerTypes.SearchStorage);
const routingStorage = container.get<RoutingStorage>(ContainerTypes.RoutingStorage);

const request = container.get<RequestRouting>(SearchTypes.Routing);
const geocoder = container.get<RequestGeocoder>(SearchTypes.Geocoding);

actions.on(ActionStates.CallDetails, (from, event) => {
	execCmd('routingCallDetails', from, event);
});

actions.on(ActionStates.HomePage, () => execCmd('RoutingView.onHomePage'));

actions.on(ActionStates.CallRouting, async (from, event) => {
	await execCmd('callRouting', from, event);
});

actions.on(ActionStates.UpdateRouting, (from, event) => {
	execCmd('updateRouting', from, event);
});

@Component({
	components: { SpeedProfileBtn, SimpleBar }
})
export default class RoutingView extends Vue {
	static generateAwesomepleteMark(item: any, query: string): string {
		return item.replace(RegExp(Awesomplete.$.regExpEscape(query.trim()), 'gi'), '<mark>$&</mark>');
	}

	@Prop() provider!: any;

	sourceValue = '';
	geocodedSource!: GeocoderEntry;
	geocodedTarget!: RouteTargetEntry;
	isActive = false;
	isEntrySet = false;
	isSearchInputFilled = false;
	entry!: RouteEntry;

	speedProfiles: SpeedProfile[] = [SpeedProfile.Car, SpeedProfile.Bicycle, SpeedProfile.Pedestrian];

	speedProfile: SpeedProfile = routingStorage.speedProfile;

	durationHours = 0;
	durationMinutes = 0;
	distance = '0';

	get config() {
		return getConfig();
	}

	i18n: I18n = deDE;

	$refs!: {
		routingContainer: HTMLDivElement;
		startPoint: HTMLInputElement;
	};

	constructor(options?: any) {
		super(options);
	}

	@Emit()
	setSpeedProfile(profile: SpeedProfile) {
		this.speedProfile = profile;
		routingStorage.speedProfile = profile;
	}

	distanceUnit() {
		return convertUnits().describe(this.i18n.lengthMeasurement).abbr;
	}

	activateSpeedProfileBtn(profile: SpeedProfile) {
		return { active: profile === this.speedProfile };
	}

	getSpeedProfileSrc(profile: SpeedProfile) {
		return this.config.routingView.speedProfile[profile];
	}

	getSpeedProfileAlt(profile: SpeedProfile) {
		return deDE.routingView.speedProfile[profile];
	}

	getWaypointSrc(waypoint: WayPoint) {
		return this.config.routingView.navigationIcon[waypoint.navigationCommand];
	}

	formatAddressLine(entry: GeocoderEntry): string {
		if (!entry || !entry.address) {
			return '';
		}
		if (entry.address.singleSlot) {
			return entry.address.singleSlot || '';
		}
		return formatAddressLine(entry.address) || '';
	}

	emptySearchInput() {
		this.sourceValue = '';
		this.$refs.startPoint.focus();
		this.isSearchInputFilled = false;
		console.log('ROUTING SEARCH VALUE EMPTY');
	}

	onClose(event: Event) {
		event.preventDefault();
		const offset = { top: 0, right: 0, bottom: 0, left: 0 };
		routingStorage.offset = offset;
		if (isRouteTargetEntry(this.geocodedTarget)) {
			this.isActive = false;
			actions.go(ActionStates.CallResults, {
				offset,
				pan: true,
				fitBounds: true
			});
		}
	}

	async onSubmit(event: Event) {
		event.preventDefault();
		this.sourceValue = this.formatAddressLine(this.geocodedSource) || searchStorage.searchTerm || '';
		this.isSearchInputFilled = true;
		await this.calculateRoute();
	}

	async onChangeSpeedProfile(event: Event, profile: SpeedProfile) {
		event.preventDefault();
		this.setSpeedProfile(profile);
		this.isSearchInputFilled = true;
		await this.calculateRoute();
	}

	autocomplete() {
		const suggestionList: AutoCompleteSuggestion[] = [];
		const autocomplete = ym.autoComplete(this.$refs.startPoint as HTMLElement, {
			dataType: 'jsonp',
			locales: this.config.autocomplete.locales,

			onEmpty: () => {
				this.geocodedSource = {
					type: 'Geocoder',
					address: {
						country: this.config.defaultCountryCode,
						isoCountry: this.config.defaultCountryCode,
						district: '',
						zip: '',
						city: '',
						cityAddon: '',
						cityPart: '',
						streetNo: ''
					},
					position: { lat: NaN, lng: NaN }
				};
			},

			onSuggestion: (geoJSON: any) => {
				const location = geoJSON.features[0].properties;
				this.geocodedSource = {
					type: 'Geocoder',
					address: {
						singleSlot: location.displayValue,
						country: location.country,
						isoCountry: location.country || 'DE',
						district: location.district,
						zip: location.zip,
						city: location.city,
						cityPart: location.cityPart,
						streetNo: location.street
					},
					position: { lat: NaN, lng: NaN }
				};

				suggestionList.splice(0);
				for (const feature of geoJSON.features) {
					suggestionList.push(feature.properties);
				}
			},
			onSelected: async (geoJSON: any) => {
				const location = geoJSON.properties.location;
				this.geocodedSource = {
					type: 'Geocoder',
					address: {
						singleSlot: geoJSON.properties.displayValue,
						country: location.country,
						isoCountry: location.country,
						district: location.district,
						zip: location.zip,
						city: location.city,
						cityAddon: location.cityAddOn,
						cityPart: location.cityPart,
						streetNo: location.street
					},
					position: { lat: NaN, lng: NaN }
				};
				this.sourceValue = geoJSON.properties.displayValue;
				this.isSearchInputFilled = true;
				await this.calculateRoute();
			}
		});

		// this is a workaround to manipulate the suggestion list items
		// TODO @Mario get rid of any!!!
		const awesomplete = (<any>autocomplete)._autocomplete;
		awesomplete.item = (item: { label: string; value: number }, query: string) => {
			const index = item.value;
			let html;
			if (query.trim() === '') {
				html = item;
			} else {
				const above = RoutingView.generateAwesomepleteMark(item, query);
				html = `<div class="awesomplete-item"><div>${above}</div></div>`;
			}

			return Awesomplete.$.create('li', {
				innerHTML: html,
				'aria-selected': 'false',
				id: 'awesomplete_list_' + index + '_item'
			});
		};
	}

	async getStations(): Promise<PositionStation[]> {
		if (!isLatLngLiteral(this.geocodedSource.position)) {
			const results = await geocoder.geocode({
				type: 'Geocoding',
				...this.geocodedSource.address
			});
			if (results && results.length) {
				const [geocoded] = results;
				this.geocodedSource.position = geocoded.position;
				this.geocodedSource.address = geocoded.address;
			}
		}
		return [
			{
				type: 'Position',
				position: this.geocodedSource.position,
				isoCountry: this.geocodedSource.address.isoCountry || this.config.defaultIsoLocale
			},
			{
				type: 'Position',
				position: this.geocodedTarget.position,
				isoCountry: this.geocodedTarget.address.isoCountry || this.config.defaultIsoLocale
			}
		];
	}

	async calculateRoute() {
		const stations = await this.getStations();
		this.entry = await request.calculateRoute({
			type: 'Routing',
			isoLocale: this.config.defaultIsoLocale,
			speedProfile: routingStorage.speedProfile,
			routeProfile: routingStorage.routeProfile,
			timeProfile: routingStorage.timeProfile,
			estimationTime: routingStorage.estimationTime || '',
			stations
		});

		routingStorage.routeEntry = this.entry;

		this.durationHours = Math.round(
			convertUnits(this.entry.duration || 0)
				.from('s')
				.to('h')
		);
		this.durationMinutes = Math.round(
			// extract rest minutes by modulo of 60 seconds * 60 minutes
			convertUnits((this.entry.duration || 0) % 3600)
				.from('s')
				.to('min')
		);
		this.distance = convertUnits(this.entry.distance || 0)
			.from('m')
			.to(this.i18n.lengthMeasurement)
			.toFixed(2);

		this.isEntrySet = true;
		if (actions.canGo(ActionStates.UpdateRouting)) {
			actions.go(ActionStates.UpdateRouting, {});
		} else {
			actions.go(ActionStates.CallRouting, {});
		}
	}

	mounted() {
		this.speedProfile = routingStorage.speedProfile;

		setCmd('RoutingView.onHomePage', () => {
			this.isActive = false;
		});

		setCmd(
			'updateResults',
			(from, event) => {
				this.isActive = false;
				this.isEntrySet = false;
			},
			false
		);

		setCmd(
			'routingCallDetails',
			(from, event) => {
				this.isActive = false;
				this.isEntrySet = false;
			},
			false
		);

		setCmd('callRouting', async (from, event?: RouteTargetEntry) => {
			if (isRouteTargetEntry(event)) {
				for (const entry of searchStorage.results.entries) {
					if (isResultEntry(entry)) {
						if (entry.idDecoded === event.idDecoded) {
							this.geocodedTarget = { ...entry, referrer: event.referrer };
							break;
						}
					}
				}

				if (this.entry === null) {
					return actions.go(ActionStates.Idle, {});
				}

				this.sourceValue = searchStorage.searchTerm || '';

				if (searchStorage.geocoding.length) {
					this.geocodedSource = searchStorage.geocoding[0];
					if (this.geocodedSource) {
						this.sourceValue =
							this.formatAddressLine(this.geocodedSource) || searchStorage.searchTerm || '';
						this.isSearchInputFilled = true;
					} else {
						this.isSearchInputFilled = false;
					}
				} else {
					const isSearchInputFilled =
						searchStorage.searchTerm !== undefined && searchStorage.searchTerm.trim() !== '';
					this.sourceValue = searchStorage.searchTerm || '';
					this.isSearchInputFilled = isSearchInputFilled;
					if (isSearchInputFilled) {
						this.geocodedSource = {
							type: 'Geocoder',
							position: searchStorage.searchCenter,
							address: {
								isoCountry: getConfig().defaultCountryCode
							}
						};
					}
				}

				this.isActive = true;
				this.$nextTick(() => {
					if (this.$refs.startPoint) {
						this.autocomplete();
					}
					if (
						this.$refs.routingContainer &&
						this.config.resultsView.scrolling.autoScroll &&
						allowDevices(this.config.resultsView.scrolling.sizes)
					) {
						this.$refs.routingContainer.scrollIntoView({
							behavior: 'smooth',
							block: 'start'
						});
					}
				});

				if (this.isSearchInputFilled) {
					await this.calculateRoute();
				}
			} else {
				actions.go(ActionStates.UpdateRouting, {});
			}
		});

		setCmd('updateRouting', (from, event?: RouteTargetEntry) => {
			if (this.$refs.routingContainer) {
				routingStorage.offset = {
					top: this.$refs.routingContainer.offsetTop,
					right: 0,
					bottom: 0,
					left: this.$refs.routingContainer.offsetLeft + this.$refs.routingContainer.clientWidth
				};

				actions.go(ActionStates.UpdateMap, {
					type: 'updateRouting',
					pan: true,
					position: routingStorage.routeEntry.position,
					offset: routingStorage.offset
				});
			}
		});
	}
}
